import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, Expand, ImageOff, X } from "lucide-react";
import type { OrientacaoProducaoData } from "../../services/controleProducaoService";
import { orientacaoProducaoImageUrl } from "../../utils/orientacaoProducaoImageUrl";

export type OrientacoesProducaoDisplayProps = {
  orientacao?: OrientacaoProducaoData | null;
  variant?: "screen" | "print";
};

type ImagemExpandida = {
  src: string;
  alt: string;
};

function suavizarCaixaAlta(texto: string): string {
  const letters = texto.replace(/[^A-Za-zÀ-ÿ]/g, "");
  if (letters.length < 6) return texto;
  const maiusculas = letters.replace(/[^A-ZÀ-Ÿ]/g, "").length;
  if (maiusculas / letters.length < 0.8) return texto;
  return texto
    .toLowerCase()
    .replace(/(^|[.!?]\s+)(\p{L})/gu, (_, sep: string, letra: string) => {
      return `${sep}${letra.toUpperCase()}`;
    });
}

function OrientacaoImagem({
  src,
  alt,
  isPrint,
  onExpand,
}: {
  src: string;
  alt: string;
  isPrint: boolean;
  onExpand?: () => void;
}) {
  const [erro, setErro] = useState(false);

  if (erro) {
    return (
      <div
        className={
          isPrint
            ? "ficha-print-orientacoes-img flex items-center justify-center bg-neutral-100 text-neutral-400"
            : "flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-neutral-100 bg-neutral-50 text-neutral-300"
        }
        title="Imagem indisponível"
      >
        <ImageOff className="h-8 w-8" strokeWidth={1.5} />
      </div>
    );
  }

  if (isPrint) {
    return (
      <img
        src={src}
        alt={alt}
        className="ficha-print-orientacoes-img"
        onError={() => setErro(true)}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onExpand}
      className="group relative w-full overflow-hidden rounded-lg border border-neutral-200 bg-white text-left shadow-sm transition hover:border-neutral-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      aria-label={`Ampliar ${alt}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setErro(true)}
        className="aspect-[4/3] w-full object-cover transition duration-200 group-hover:scale-[1.02]"
      />
      <span className="pointer-events-none absolute inset-0 bg-neutral-900/0 transition group-hover:bg-neutral-900/10" />
      <span className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-neutral-600 opacity-0 shadow-sm backdrop-blur-sm transition group-hover:opacity-100">
        <Expand className="h-3.5 w-3.5" />
        Ampliar
      </span>
    </button>
  );
}

function OrientacaoImagemLightbox({
  imagem,
  onClose,
}: {
  imagem: ImagemExpandida;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={imagem.alt}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
        aria-label="Fechar"
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="max-h-[90vh] w-full max-w-5xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={imagem.src}
          alt={imagem.alt}
          className="mx-auto max-h-[85vh] w-full rounded-lg object-contain shadow-2xl"
        />
        <p className="mt-3 text-center text-sm text-white/80">{imagem.alt}</p>
      </div>
    </div>,
    document.body
  );
}

const OrientacoesProducaoDisplay: React.FC<OrientacoesProducaoDisplayProps> = ({
  orientacao,
  variant = "screen",
}) => {
  const texto = orientacao?.texto?.trim() ?? "";
  const imagens = orientacao?.imagens ?? [];
  const [imagemExpandida, setImagemExpandida] = useState<ImagemExpandida | null>(
    null
  );
  const linhasTexto = useMemo(
    () =>
      texto
        .split(/\n+/)
        .map((l) => suavizarCaixaAlta(l.trim()))
        .filter(Boolean),
    [texto]
  );

  if (!texto && imagens.length === 0) {
    return null;
  }

  const isPrint = variant === "print";

  if (isPrint) {
    return (
      <section
        className={`ficha-print-orientacoes ficha-print-orientacoes--n${Math.min(imagens.length, 4) || 0}`}
      >
        <div className="ficha-print-orientacoes-title">Orientações</div>
        {texto && (
          <p className="ficha-print-orientacoes-texto">{texto}</p>
        )}
        {imagens.length > 0 && (
          <div className="ficha-print-orientacoes-imagens">
            {imagens.map((img) => (
              <OrientacaoImagem
                key={img.id}
                src={orientacaoProducaoImageUrl(img.url)}
                alt={`Orientação ${img.ordem}`}
                isPrint
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <>
      <section className="mx-auto mt-4 w-full max-w-5xl">
        <div className="overflow-hidden rounded-xl border border-primary/10 bg-white shadow-sm">
          <header className="flex items-center gap-3 border-b border-primary/10 bg-[#fff5f5] px-4 py-3 sm:px-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <BookOpen className="h-4 w-4 text-primary" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">
                Orientações
              </h3>
              <p className="text-xs text-neutral-500">
                Recados do dia para a produção
              </p>
            </div>
          </header>

          {linhasTexto.length > 0 && (
            <div className="space-y-3 px-4 py-4 sm:px-5">
              {linhasTexto.map((linha, index) => (
                <p
                  key={`${index}-${linha.slice(0, 24)}`}
                  className="text-sm leading-6 text-neutral-700"
                >
                  {linha}
                </p>
              ))}
            </div>
          )}

          {imagens.length > 0 && (
            <div
              className={`grid grid-cols-1 gap-3 px-4 pb-4 sm:grid-cols-2 sm:px-5 ${
                imagens.length >= 3 ? "lg:grid-cols-3" : ""
              } ${
                linhasTexto.length > 0
                  ? "border-t border-neutral-100 pt-4"
                  : "pt-4"
              }`}
            >
              {imagens.map((img) => {
                const src = orientacaoProducaoImageUrl(img.url);
                const alt = `Orientação ${img.ordem}`;
                return (
                  <OrientacaoImagem
                    key={img.id}
                    src={src}
                    alt={alt}
                    isPrint={false}
                    onExpand={() => setImagemExpandida({ src, alt })}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {imagemExpandida && (
        <OrientacaoImagemLightbox
          imagem={imagemExpandida}
          onClose={() => setImagemExpandida(null)}
        />
      )}
    </>
  );
};

export default OrientacoesProducaoDisplay;
